import * as THREE from "three";
import { gsap } from "gsap";
import Experience from "../Experience.js";

export default class AboutMiiHead {
  constructor() {
    this.experience = new Experience();
    this.scene = this.experience.scene;
    this.resources = this.experience.resources;
    this.raycaster = this.experience.raycaster;

    // Constants
    this.BASE_ROTATION = -Math.PI * + 0.16;
    this.MOUSE_SENSITIVITY = { x: 0.3, y: 0.2 };
    this.BLINK_DURATION = 150;
    this.PINCH_DURATION = 500;
    this.LERP_FACTOR = 0.05;

    // State
    this.mouse = { x: 0, y: 0 };
    this.targetRotation = { x: 0, y: this.BASE_ROTATION };
    this.pinchState = "normal";
    this.pinchAnimation = { progress: 0, bobIntensity: 0 };
    this.isVisible = true;
    this.blinkTimeout = null; // Store blink timeout to clear it if needed

    // Animation state for section transitions
    this.animationState = "hidden"; // "hidden", "animating-in", "visible", "animating-out"

    // 3-part animation progress tracking
    this.entranceProgress = 0;  // 0-1 for entrance animation
    this.toCenterProgress = 0;  // 0-1 for move to center
    this.exitProgress = 0;      // 0-1 for exit animation
    this.currentAnimationPhase = "none";

    // Legacy support
    this.sectionProgress = 0;

    // Bound methods
    this.boundMouseMove = this.handleMouseMove.bind(this);
    this.boundClick = this.handleClick.bind(this);

    this.setModels();
    this.startBlinking();
    this.setupEventListeners();
  }

  setModels() {
    if (
      this.resources.items.aboutMiiHeadModel &&
      this.resources.items.aboutMiiHeadBlinkModel &&
      this.resources.items.aboutMiiHeadPinchModel
    ) {
      // Set up normal eyes model
      this.normalModel = this.resources.items.aboutMiiHeadModel.scene;
      this.setupModel(this.normalModel);
      this.normalModel.visible = true;

      // Set up blink eyes model
      this.blinkModel = this.resources.items.aboutMiiHeadBlinkModel.scene;
      this.setupModel(this.blinkModel);
      this.blinkModel.visible = false; // Hidden by default

      // Set up pinch reaction model
      this.pinchModel = this.resources.items.aboutMiiHeadPinchModel.scene;
      this.setupModel(this.pinchModel);
      this.pinchModel.visible = false; // Hidden by default

      // Add all to scene
      this.scene.add(this.normalModel);
      this.scene.add(this.blinkModel);
      this.scene.add(this.pinchModel);
    }
  }

  setupModel(model) {
    const box = new THREE.Box3().setFromObject(model);
    const center = box.getCenter(new THREE.Vector3());

    model.scale.set(0.07, 0.07, 0.07);

    // Position for About Me section (right side)
    const aboutPosition = {
      x: -center.x * 0.09 + 23,
      y: -center.y * 0.09 + 5,
      z: -center.z * 0.09 - 1,
    };

    // Position for Skills section (center)
    const skillsPosition = {
      x: -center.x * 0.09 + 18,
      y: -center.y * 0.09 + 5,
      z: -center.z * 0.09 - 1,
    };

    model.position.set(aboutPosition.x, aboutPosition.y, aboutPosition.z);
    model.rotation.y = this.BASE_ROTATION;

    // Store all positions for the 3-part animation
    model.userData.hiddenRightPosition = new THREE.Vector3(
      aboutPosition.x + 10, // Start off-screen to the right
      aboutPosition.y,
      aboutPosition.z
    );
    model.userData.aboutPosition = new THREE.Vector3(aboutPosition.x, aboutPosition.y, aboutPosition.z);
    model.userData.skillsPosition = new THREE.Vector3(skillsPosition.x, skillsPosition.y, skillsPosition.z);
    model.userData.hiddenUpPosition = new THREE.Vector3(
      skillsPosition.x,
      skillsPosition.y + 15, // Exit upward
      skillsPosition.z
    );
    model.userData.originalScale = model.scale.clone();
    model.userData.hiddenScale = new THREE.Vector3(0.01, 0.01, 0.01); // Very small when hidden

    // Start hidden off-screen to the right
    model.position.copy(model.userData.hiddenRightPosition);
    model.scale.copy(model.userData.hiddenScale);

    // Phase tracking for 3-part animation
    model.userData.currentPhase = "hidden"; // "hidden", "about", "skills", "exited"

    model.traverse((child) => {
      if (child.isMesh) {
        child.castShadow = true;
        child.receiveShadow = false;
      }
    });
  }

  startBlinking() {
    const scheduleNextBlink = () => {
      const delay = 2000 + Math.random() * 3000;
      setTimeout(() => {
        this.blink();
        scheduleNextBlink();
      }, delay);
    };
    scheduleNextBlink();
  }

  blink() {
    if (this.pinchState !== "normal") return;

    if (this.normalModel && this.blinkModel) {
      this.normalModel.visible = false;
      this.blinkModel.visible = true;

      // Clear any existing blink timeout
      if (this.blinkTimeout) {
        clearTimeout(this.blinkTimeout);
      }

      this.blinkTimeout = setTimeout(() => {
        if (this.pinchState === "normal") {
          this.normalModel.visible = true;
          this.blinkModel.visible = false;
        }
        this.blinkTimeout = null;
      }, this.BLINK_DURATION);
    }
  }

  setupEventListeners() {
    window.addEventListener("mousemove", this.boundMouseMove);
    window.addEventListener("click", this.boundClick);
  }

  handleMouseMove(event) {
    this.mouse.x = (event.clientX / window.innerWidth) * 2 - 1;
    this.mouse.y = -(event.clientY / window.innerHeight) * 2 + 1;

    this.targetRotation.x = -this.mouse.y * this.MOUSE_SENSITIVITY.y;
    this.targetRotation.y =
      this.BASE_ROTATION + this.mouse.x * this.MOUSE_SENSITIVITY.x;
  }

  handleClick(event) {
    if (this.pinchState !== "normal") return;

    const visibleModels = [];
    if (this.normalModel?.visible) visibleModels.push(this.normalModel);
    if (this.blinkModel?.visible) visibleModels.push(this.blinkModel);

    const intersects = this.raycaster.castRay(visibleModels, true);
    if (intersects.length > 0) {
      this.triggerPinchReaction();
    }
  }

  triggerPinchReaction() {
    this.pinchState = "pinching";
    this.pinchAnimation.progress = 0;

    // Clear any pending blink timeout to prevent interference
    if (this.blinkTimeout) {
      clearTimeout(this.blinkTimeout);
      this.blinkTimeout = null;
    }

    this.normalModel.visible = false;
    this.blinkModel.visible = false;
    this.pinchModel.visible = true;
  }

  setVisibility(visible) {
    this.isVisible = visible;

    if (this.normalModel) {
      this.normalModel.visible = visible && this.pinchState === "normal";
    }
    if (this.blinkModel) {
      this.blinkModel.visible = visible && this.blinkModel.visible;
    }
    if (this.pinchModel) {
      this.pinchModel.visible = visible && this.pinchState === "pinching";
    }
  }

  updatePinchAnimation() {
    if (this.pinchState === "normal") return;

    const deltaTime = this.experience.time.delta;
    this.pinchAnimation.progress += deltaTime;

    if (this.pinchState === "pinching") {
      const bobProgress = this.pinchAnimation.progress / this.PINCH_DURATION;
      this.pinchAnimation.bobIntensity =
        Math.sin(bobProgress * Math.PI * 3) * 0.3;

      if (this.pinchAnimation.progress >= this.PINCH_DURATION) {
        this.pinchState = "normal";
        this.pinchAnimation.progress = 0;
        this.pinchAnimation.bobIntensity = 0;
        this.pinchModel.visible = false;
        this.normalModel.visible = true;
      }
    }
  }

  cleanup() {
    window.removeEventListener("mousemove", this.boundMouseMove);
    window.removeEventListener("click", this.boundClick);
  }

  // Animation methods for section transitions
  animateIn(duration = 1.2) {
    if (
      this.animationState === "visible" ||
      this.animationState === "animating-in"
    )
      return;

    this.animationState = "animating-in";

    const models = [this.normalModel, this.blinkModel, this.pinchModel];

    models.forEach((model) => {
      if (
        model &&
        model.userData.originalPosition &&
        model.userData.hiddenPosition
      ) {
        // Animate position
        gsap.to(model.position, {
          x: model.userData.originalPosition.x,
          y: model.userData.originalPosition.y,
          z: model.userData.originalPosition.z,
          duration: duration,
          ease: "power2.out",
        });

        // Animate scale
        gsap.to(model.scale, {
          x: model.userData.originalScale.x,
          y: model.userData.originalScale.y,
          z: model.userData.originalScale.z,
          duration: duration,
          ease: "back.out(1.7)", // Bouncy scale animation
          onComplete: () => {
            this.animationState = "visible";
          },
        });
      }
    });
  }

  animateOut(duration = 1.2) {
    if (
      this.animationState === "hidden" ||
      this.animationState === "animating-out"
    )
      return;

    this.animationState = "animating-out";

    const models = [this.normalModel, this.blinkModel, this.pinchModel];

    models.forEach((model) => {
      if (model && model.userData.hiddenPosition) {
        // Animate position
        gsap.to(model.position, {
          x: model.userData.hiddenPosition.x,
          y: model.userData.hiddenPosition.y,
          z: model.userData.hiddenPosition.z,
          duration: duration,
          ease: "power2.in",
        });

        // Animate scale
        gsap.to(model.scale, {
          x: model.userData.hiddenScale.x,
          y: model.userData.hiddenScale.y,
          z: model.userData.hiddenScale.z,
          duration: duration,
          ease: "back.in(1.7)", // Bouncy scale animation
          onComplete: () => {
            this.animationState = "hidden";
          },
        });
      }
    });
  }

  // 3-part animation system
  setScrollProgress(phase, progress) {
    this.currentAnimationPhase = phase;

    switch (phase) {
      case "entrance":
        this.entranceProgress = Math.max(0, Math.min(1, progress));
        break;
      case "toCenter":
        this.toCenterProgress = Math.max(0, Math.min(1, progress));
        break;
      case "exit":
        this.exitProgress = Math.max(0, Math.min(1, progress));
        break;
    }
  }

  // Legacy method for backwards compatibility - remove after transition
  // setScrollProgress(progress) {
  //   this.sectionProgress = Math.max(0, Math.min(1, progress));
  // }
  update() {
    this.updatePinchAnimation();

    if (this.normalModel && this.blinkModel && this.pinchModel) {
      const time = this.experience.time.elapsed * 0.001;

      // Idle animations
      const idleX = Math.sin(time * 0.5) * 0.1;
      const idleY = Math.sin(time * 0.3) * 0.15;

      const finalRotationX = this.targetRotation.x + idleX;
      const finalRotationY = this.targetRotation.y + idleY;

      const models = [this.normalModel, this.blinkModel, this.pinchModel];

      models.forEach((model) => {
        // Calculate target rotation based on current phase
        let targetRotationY = this.targetRotation.y;

        if (this.exitProgress > 0) {
          // During exit phase, maintain skills rotation (don't jump back)
          const skillsRotation = 0;
          targetRotationY = skillsRotation + this.mouse.x * this.MOUSE_SENSITIVITY.x;
        } else if (model.userData.currentPhase === "skills" && this.toCenterProgress > 0) {
          // During skills phase, interpolate rotation to 0
          const skillsRotation = 0;
          targetRotationY = THREE.MathUtils.lerp(
            this.BASE_ROTATION + this.mouse.x * this.MOUSE_SENSITIVITY.x,
            skillsRotation + this.mouse.x * this.MOUSE_SENSITIVITY.x,
            this.toCenterProgress
          );
        }

        model.rotation.x = THREE.MathUtils.lerp(
          model.rotation.x,
          finalRotationX,
          this.LERP_FACTOR
        );
        model.rotation.y = THREE.MathUtils.lerp(
          model.rotation.y,
          targetRotationY + idleY,
          this.LERP_FACTOR
        );

        // Update positions and scale based on 3-part animation system
        if (model.userData.hiddenRightPosition && model.userData.originalScale) {
          let targetPosition = model.userData.hiddenRightPosition.clone();
          let targetScale = model.userData.hiddenScale.clone();
          let currentPhase = "hidden";

          // Determine current position based on animation progress
          if (this.exitProgress > 0) {
            // Phase 3: Exiting upward
            targetPosition.lerpVectors(
              model.userData.skillsPosition,
              model.userData.hiddenUpPosition,
              this.exitProgress
            );
            // Keep the skills scale during exit (don't jump back to original)
            targetScale = new THREE.Vector3(0.06, 0.06, 0.06);
            currentPhase = "exiting";
          } else if (this.toCenterProgress > 0) {
            // Phase 2: Moving to center
            targetPosition.lerpVectors(
              model.userData.aboutPosition,
              model.userData.skillsPosition,
              this.toCenterProgress
            );
            targetScale = model.userData.originalScale.clone();

            // Animate scale down to 0.5 during center transition
            const skillsScale = new THREE.Vector3(0.06, 0.06, 0.06); // Smaller scale for skills center
            targetScale.lerpVectors(
              model.userData.originalScale,
              skillsScale,
              this.toCenterProgress
            );

            currentPhase = "skills";
          } else if (this.entranceProgress > 0) {
            // Phase 1: Entrance animation
            targetPosition.lerpVectors(
              model.userData.hiddenRightPosition,
              model.userData.aboutPosition,
              this.entranceProgress
            );
            targetScale.lerpVectors(
              model.userData.hiddenScale,
              model.userData.originalScale,
              this.entranceProgress
            );
            currentPhase = "about";
          }

          // Update model phase
          model.userData.currentPhase = currentPhase;

          // Apply the calculated position and scale
          model.position.copy(targetPosition);
          model.scale.copy(targetScale);

          // Apply pinch bobbing on top of the base position
          if (this.pinchState !== "normal") {
            model.position.y += this.pinchAnimation.bobIntensity * 0.5;
          }
        }
      });
    }
  }
}
